import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, useUnit, api } from "@/App";
import { toast } from "sonner";
import NotificationBell from "@/components/NotificationBell";
import { 
  Wrench, 
  LayoutDashboard, 
  Ticket, 
  Plus,
  Settings,
  LogOut,
  ChevronLeft,
  Menu,
  ClipboardCheck,
  Users,
  Building2,
  Headset,
  Briefcase,
  FileText,
  Download,
  BarChart2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useBranding } from "@/hooks/useBranding";

const UNIT_NAMES = {
  qnc: "QNC",
  diner_dfw: "DINER DFW",
  diner_fl: "DINER FL",
  textaco: "Textaco",
};

export default function Sidebar({ unitId }) {
  const { user, logout } = useAuth();
  const { currentUnit } = useUnit();
  const { getMainLogoUrl, getMarketLogoUrl } = useBranding();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  const mainLogoUrl = getMainLogoUrl();
  const marketLogoUrl = getMarketLogoUrl(unitId);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleBackToMarkets = () => {
    navigate("/select-market");
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await api.get(`/tickets/export?unit_id=${unitId}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `tickets_${unitId}_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Export downloaded successfully");
    } catch (error) {
      toast.error("Failed to export tickets");
    } finally {
      setExporting(false);
    }
  };

  const isActive = (path) => location.pathname.includes(path);

  const getNavItems = () => {
    const items = [
      { 
        label: "Dashboard", 
        path: `/dashboard/${unitId}`, 
        icon: LayoutDashboard,
        roles: ["all"]
      },
      { 
        label: "All Tickets", 
        path: `/tickets/${unitId}`, 
        icon: Ticket,
        roles: ["all"]
      },
    ];

    // Role-specific items
    const ticketCreatorRoles = ["Store Manager", "District Manager", "District Operations", "SystemAdmin"];
    if (ticketCreatorRoles.includes(user?.role)) {
      items.push({ 
        label: "New Ticket", 
        path: `/new-ticket/${unitId}`, 
        icon: Plus,
        roles: ticketCreatorRoles
      });
    }

    if (user?.role === "HelpDesk" || user?.role === "SystemAdmin") {
      items.push({ 
        label: "Help Desk Queue", 
        path: `/helpdesk/${unitId}`, 
        icon: Headset,
        roles: ["HelpDesk", "SystemAdmin"]
      });
      items.push({ 
        label: "Vendors", 
        path: `/vendors/${unitId}`, 
        icon: Briefcase,
        roles: ["HelpDesk", "SystemAdmin"]
      });
    }

    if (user?.role === "Technician" || user?.role === "Vendor") {
      items.push({ 
        label: "My Assignments", 
        path: `/my-tickets/${unitId}`, 
        icon: Briefcase,
        roles: ["Technician", "Vendor"]
      });
    }

    if (["District Manager", "District Operations", "Finance", "President", "SystemAdmin"].includes(user?.role)) {
      items.push({ 
        label: "Pending Approvals", 
        path: `/approvals/${unitId}`, 
        icon: ClipboardCheck,
        roles: ["District Manager", "District Operations", "Finance", "President", "SystemAdmin"]
      });
      items.push({
        label: "Analytics",
        path: `/analytics/${unitId}`,
        icon: BarChart2,
        roles: ["District Manager", "District Operations", "Finance", "President", "SystemAdmin"]
      });
    }

    return items;
  };

  const navItems = getNavItems();

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo & Unit */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center overflow-hidden">
              {mainLogoUrl ? (
                <img src={mainLogoUrl} alt="Efacilicare" className="w-full h-full object-contain" data-testid="sidebar-main-logo" />
              ) : (
                <Wrench className="w-6 h-6 text-white" />
              )}
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">Efacilicare</h1>
              <p className="text-xs text-gray-500">Maintenance System</p>
            </div>
          </div>
          <NotificationBell />
        </div>
        
        {/* Current Unit */}
        <div className="flex items-center gap-2 p-2 bg-gray-100 rounded-lg">
          {marketLogoUrl ? (
            <img src={marketLogoUrl} alt={unitId} className="w-5 h-5 object-contain rounded" data-testid="sidebar-market-logo" />
          ) : (
            <Building2 className="w-4 h-4 text-gray-500" />
          )}
          <span className="text-sm font-medium text-gray-700">
            {UNIT_NAMES[unitId] || currentUnit?.name || unitId}
          </span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = location.pathname === item.path;
          
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setMobileOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                active 
                  ? "bg-indigo-50 text-indigo-700" 
                  : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
              }`}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <Icon className="w-5 h-5" />
              <span className="font-medium">{item.label}</span>
            </Link>
          );
        })}

        {/* Export Button */}
        <button
          onClick={handleExport}
          disabled={exporting}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
          data-testid="export-tickets-btn"
        >
          <Download className="w-5 h-5" />
          <span className="font-medium">{exporting ? "Exporting..." : "Export to Excel"}</span>
        </button>

        {/* Admin Panel Link */}
        {user?.role === "SystemAdmin" && (
          <Link
            to="/admin"
            onClick={() => setMobileOpen(false)}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
              isActive("/admin")
                ? "bg-indigo-50 text-indigo-700"
                : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
            }`}
            data-testid="nav-admin-panel"
          >
            <Settings className="w-5 h-5" />
            <span className="font-medium">Admin Panel</span>
          </Link>
        )}
      </nav>

      {/* Bottom Section */}
      <div className="p-4 border-t border-gray-200 space-y-2">
        {/* Back to Markets */}
        <button
          onClick={handleBackToMarkets}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-600 hover:bg-gray-100 hover:text-gray-900 transition-colors"
          data-testid="back-to-markets-btn"
        >
          <ChevronLeft className="w-5 h-5" />
          <span className="font-medium">Switch Market</span>
        </button>

        {/* User Info */}
        <div className="px-3 py-2">
          <p className="text-sm font-medium text-gray-900">{user?.full_name}</p>
          <p className="text-xs text-gray-500">{user?.role}</p>
        </div>

        {/* Logout */}
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
          data-testid="sidebar-logout-btn"
        >
          <LogOut className="w-5 h-5" />
          <span className="font-medium">Sign Out</span>
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-64 bg-white border-r border-gray-200 flex-col flex-shrink-0">
        <SidebarContent />
      </aside>

      {/* Mobile Header & Drawer */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center overflow-hidden">
              {mainLogoUrl ? (
                <img src={mainLogoUrl} alt="Efacilicare" className="w-full h-full object-contain" />
              ) : (
                <Wrench className="w-5 h-5 text-white" />
              )}
            </div>
            <span className="font-bold text-gray-900">Efacilicare</span>
          </div>
          
          <div className="flex items-center gap-2">
            <NotificationBell />
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="sm" data-testid="mobile-menu-btn">
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="p-0 w-72">
                <SidebarContent />
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      {/* Mobile spacer */}
      <div className="md:hidden h-16"></div>
    </>
  );
}
